/**
 * Chat Payment Page
 *
 * Wrapper page for CHAT interaction type.
 * Renders AgentProgressUIChatTimeline component with session data.
 */

import { useState, useCallback } from 'react';
import { AgentProgressUIChatTimeline } from '@components/AgentProgressUIChatTimeline';
import { SuccessScreen } from '@components/AgentProgressUIChatTimeline/screens/SuccessScreen';
import { APP_CONFIG } from '@utils/constants';
import { appendQueryParams } from '@utils/appendQueryParams';
import { logger } from '@utils/logger';
import type { Session } from '@/types/session.types';
import type { TimelineEvent } from '@/types/chat-timeline.types';

interface ChatPaymentPageProps {
  session: Session;
  /** Pre-validated payment session forwarded to useAgentApi → useAuthentication */
  initialSession?: PaymentSession;
}

/**
 * Detect mock scenario from sessionId
 *
 * Checks sessionId for keywords to determine scenario:
 * - saved-card → 'saved-card'
 * - apple-pay → 'apple-pay'
 * - google-pay → 'google-pay'
 * - paypal → 'paypal'
 * - decline → 'decline'
 * - expire → 'expire'
 * - error-retry → 'error-retry'
 * - error-declined → 'error-declined'
 * - error-max-attempts → 'error-max-attempts'
 * - default → 'new-card'
 */
function detectMockScenario(
  sessionId: string
):
  | 'new-card'
  | 'saved-card'
  | 'apple-pay'
  | 'google-pay'
  | 'paypal'
  | 'decline'
  | 'expire'
  | 'error-retry'
  | 'error-declined'
  | 'error-max-attempts' {
  const id = sessionId.toLowerCase();

  // Error scenarios (check first for specificity)
  if (id.includes('error-max-attempts') || id.includes('max-attempts')) {
    return 'error-max-attempts';
  }

  if (id.includes('error-declined') || id.includes('error-decline')) {
    return 'error-declined';
  }

  if (id.includes('error-retry')) {
    return 'error-retry';
  }

  // Regular scenarios
  if (id.includes('saved-card') || id.includes('savedcard')) {
    return 'saved-card';
  }

  if (id.includes('apple-pay') || id.includes('applepay')) {
    return 'apple-pay';
  }

  if (id.includes('google-pay') || id.includes('googlepay')) {
    return 'google-pay';
  }

  if (id.includes('paypal')) {
    return 'paypal';
  }

  if (id.includes('decline')) {
    return 'decline';
  }

  if (id.includes('expire') || id.includes('timeout')) {
    return 'expire';
  }

  return 'new-card';
}

/**
 * Chat Payment Page
 *
 * CHAT interaction type - displays chat timeline UI
 */
interface ChatSuccessData {
  amount: number;
  currency: string;
  paymentId?: string;
  merchantTransactionId?: string;
  timestamp: string;
}

export default function ChatPaymentPage({
  session,
  initialSession,
}: ChatPaymentPageProps) {
  const mockScenario = APP_CONFIG.USE_MOCK_DATA
    ? detectMockScenario(session.sessionId)
    : undefined;

  const [successData, setSuccessData] = useState<ChatSuccessData | null>(null);

  const handleSessionComplete = useCallback(
    (timelineData: TimelineEvent[]) => {
      const returnUrl = initialSession?.agentReturnUrl;
      if (returnUrl) {
        const fullReturnUrl = appendQueryParams(returnUrl, {
          paymentId: initialSession?.paymentId,
        });
        logger.info('Redirecting agent to return URL after CHAT completion', {
          sessionId: session.sessionId,
        });
        window.location.href = fullReturnUrl;
        return;
      }

      // Terminal decline: the timeline component already emitted the DECLINED
      // postMessage. Do NOT show the success screen — the agent stays on the
      // timeline showing "Payment declined" (PAS-32538).
      const declined = timelineData.some(
        (e) => e.eventType === 'PAYMENT_COMPLETE' && e.failed
      );
      if (declined) {
        logger.info('CHAT payment declined — staying on timeline', {
          sessionId: session.sessionId,
        });
        return;
      }

      const completedEvent = timelineData.find(
        (e) => e.eventType === 'PAYMENT_COMPLETE' && e.completed
      );
      const merchantTransactionId =
        initialSession?.merchantTransactionId ??
        (completedEvent?.transactionId !== session.sessionId
          ? completedEvent?.transactionId
          : undefined);

      setSuccessData({
        amount: session.amount,
        currency: session.currency,
        paymentId: initialSession?.paymentId,
        merchantTransactionId,
        timestamp: new Date().toISOString(),
      });
    },
    [
      initialSession?.agentReturnUrl,
      initialSession?.paymentId,
      initialSession?.merchantTransactionId,
      session.sessionId,
      session.amount,
      session.currency,
    ]
  );

  const handleSessionCancel = useCallback(() => {
    logger.info('CHAT payment cancelled', { sessionId: session.sessionId });
    const returnUrl = initialSession?.agentReturnUrl;
    if (returnUrl) {
      const fullReturnUrl = appendQueryParams(returnUrl, {
        paymentId: initialSession?.paymentId,
      });
      logger.info('Redirecting agent to return URL after CHAT cancellation', {
        sessionId: session.sessionId,
      });
      window.location.href = fullReturnUrl;
    }
  }, [
    initialSession?.agentReturnUrl,
    initialSession?.paymentId,
    session.sessionId,
  ]);

  if (successData) {
    return (
      <SuccessScreen
        amount={successData.amount}
        currency={successData.currency}
        paymentId={successData.paymentId}
        merchantTransactionId={successData.merchantTransactionId}
        timestamp={successData.timestamp}
      />
    );
  }

  return (
    <AgentProgressUIChatTimeline
      sessionId={session.sessionId}
      customerId={session.customerId}
      interactionType="CHAT"
      amount={session.amount}
      currency={session.currency}
      useMockData={APP_CONFIG.USE_MOCK_DATA}
      mockScenario={mockScenario}
      initialSession={initialSession}
      onSessionComplete={handleSessionComplete}
      onSessionCancel={handleSessionCancel}
    />
  );
}
